import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import entitlementsService from '../api/entitlementsService';
import type { Entitlement, CreateEntitlementDto, UpdateEntitlementDto } from '../types/types';

export const useEntitlements = () => {
    return useQuery<Entitlement[]>({
        queryKey: ['entitlements'],
        queryFn: () => entitlementsService.getAll(),
        refetchOnWindowFocus: false,
    });
};

export const useEntitlement = (id: number | undefined) => {
    return useQuery<Entitlement>({
        queryKey: ['entitlement', id],
        queryFn: () => entitlementsService.getById(id!),
        enabled: id != null,
        refetchOnWindowFocus: false,
    });
};

export const useCreateEntitlement = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (dto: CreateEntitlementDto) => entitlementsService.create(dto),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['entitlements'] }),
    });
};

export const useUpdateEntitlement = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, dto }: { id: number; dto: UpdateEntitlementDto }) =>
            entitlementsService.update(id, dto),
        onSuccess: (_, { id }) => {
            qc.invalidateQueries({ queryKey: ['entitlements'] });
            qc.invalidateQueries({ queryKey: ['entitlement', id] });
        },
    });
};

export const useDeleteEntitlement = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => entitlementsService.remove(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['entitlements'] });
            qc.invalidateQueries({ queryKey: ['testConfigurations'] });
            qc.invalidateQueries({ queryKey: ['testCategories'] });
        },
    });
};
